import { EventEmitter, inject, Injectable } from "@angular/core";
import { TooltipCoinOptions, TooltipService, TooltipTradableAction } from "@main-services/tooltip.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Cryptocoin, Fiat, Offer, PriceAlert } from "@models";
import { Tradable } from "@models/interfaces/tradable";
import { AccountService } from "@modules/account/services/account.service";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";
import { ButtonAction } from "@modules/discover/constants/actions";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { SendEntryPointService } from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { PricewatchAddModalComponent } from "@modules/shared/modals/pricewatch-add-modal/components/pricewatch-add-modal.component";
import { INDEX_TYPE, TOKEN_BEST_ID } from "@helpers/constants";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { Modal, ModalService } from "@core-services/modal.service";
import { WalletSelectionModalComponent } from "@modules/main/components/tradable-to-view-item/tradable-to-wallet/wallet-selection-modal/wallet-selection-modal.component";
import { Best2TermsModalComponent } from "@modules/trade/modals/best-2-terms-modal/components/best-2-terms-modal.component";
import { TradeSwapComponent } from "@modules/trade/modals/trade-swap-modal/trade-swap.component";
import { InternalTransaction, SendTransaction } from "@models/transaction";
import { TransferModalComponent } from "@modules/main/components/step-summary/transfer-modal/transfer-modal.component";
import { CryptowalletDetailsComponent } from "@modules/main/components/wallet-edit/wallet-details/cryptowallet-details.component";
import { TradeSavingsComponent } from "@modules/trade/modals/trade-savings-modal/trade-savings-modal.component";
import { TRADE_ACTION } from "@modules/action-buttons/action-buttons.config";
import { StockTestService } from "@main-services/stock-test.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { UserService } from "@main-services/user.service";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { take } from "rxjs/operators";
import { combineLatest } from "rxjs";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { CryptoDepositFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit.facade";
import { CryptoDepositTrackingFacade } from "@modules/+new/payments/features/crypto-deposit/domain/+state/crypto-deposit-tracking.facade";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { SecurityToken } from "@models/asset/cryptocoins/currencies/security_token";
import { MarginTradingEntryHelper } from "@modules/margin-trading/services/marign-trading-entry.helper";
import { TradeType } from "@gql/offer";

export interface ActionButtonConfig {
	action: ButtonAction;
	isDisabled: boolean;
	tooltip: string;
	tooltipAmount: string; // used for argument in translation
}

// This class contains all the legacy code for action buttons, but made to work without inheritance structure
@Injectable()
export class ActionButtonsFacade {
	constructor(
		private readonly tooltipService: TooltipService,
		private readonly tradableWalletListService: TradableWalletListService,
		private readonly accountService: AccountService,
		private readonly sendEntryPoint: SendEntryPointService,
		private readonly userFiatFundsService: UserFiatFundsService,
		private readonly fiatDepositEntryService: FiatDepositEntryService,
		private readonly modalService: ModalService,
		private readonly stockTestService: StockTestService,
		private readonly stockTestHelperService: StockTestHelperService,
		private readonly userService: UserService,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly fiatWalletService: FiatWalletService,
		private readonly cryptoDepositFacade: CryptoDepositFacade,
		private readonly checkoutEntryHelper: CheckoutEntryHelperService,
		private readonly termsAndConditionsService: TermsAndConditionsService,
		private readonly marginTradingEntryHelper: MarginTradingEntryHelper
	) {}

	protected readonly cryptoDepositTrackingFacade = inject(CryptoDepositTrackingFacade);

	public performAction(action: string, tradable: Tradable, fiat: Fiat): void {
		combineLatest({
			checkoutFlag: this.launchDarklyService.isActive$(LD_FLAG.CHECKOUT_FLOW_ENABLED),
			isMultichainEnabled: this.launchDarklyService.isActive$(LD_FLAG.DEPOSIT_MULTICHAIN),
			isMultichainWithdrawalEnabled: this.launchDarklyService.isActive$(LD_FLAG.WITHDRAWAL_MULTICHAIN),
			newTermsAndConditions: this.termsAndConditionsService.isDynamictermAndConditionActive$
		})
			.pipe(take(1))
			.subscribe(
				({ checkoutFlag, isMultichainEnabled, isMultichainWithdrawalEnabled, newTermsAndConditions }) => {
					if (action === ButtonAction.MARGIN_TRADE) {
						this.marginTradingEntryHelper.openMarginTradingFlow(TradeType.Buy, tradable.getId());
						return;
					}

					if (
						!this.userFiatFundsService.hasFiatWalletsWithFunds() &&
						!checkoutFlag &&
						action === TRADE_TYPE.BUY
					) {
						this.fiatDepositEntryService.openDepositNow();
					} else {
						if (isMultichainEnabled && action === TRADE_TYPE.DEPOSIT) {
							if (tradable.getId() === TOKEN_BEST_ID && this.accountService.showBest2TermsOnBestTrade()) {
								if (newTermsAndConditions) {
									this.navigateToNextScreen(tradable);
								} else {
									this.handleBest2Terms().subscribe((res) => {
										if (res.data === "accepted") {
											this.navigateToNextScreen(tradable);
										}
									});
								}
							} else {
								this.navigateToNextScreen(tradable);
							}
						} else {
							this.trade(
								action,
								tradable,
								fiat,
								isMultichainWithdrawalEnabled,
								checkoutFlag,
								newTermsAndConditions
							);
						}
					}
				}
			);
	}

	private navigateToNextScreen(tradable: Tradable): void {
		this.cryptoDepositFacade.availableCryptoList$.pipe(take(1)).subscribe((list) => {
			const currentDepositCrypto = list.find(
				(item) => item.symbol.toUpperCase() === tradable.getAssetSymbol().toUpperCase()
			);
			if (currentDepositCrypto) {
				this.cryptoDepositTrackingFacade.trackDepositFlowStarted();
				if (currentDepositCrypto.networks.length > 1) {
					this.cryptoDepositFacade.navigateToNetworkSelection(currentDepositCrypto.symbol);
				} else {
					this.cryptoDepositFacade.navigateToDepositSummary(
						currentDepositCrypto.symbol,
						currentDepositCrypto.networks[0].id
					);
				}
			}
		});
	}

	public getActionButtonConfigs(
		tradable: Tradable,
		walletBalances: WalletsBalanceStatus,
		fiat: Fiat,
		highestBalanceWallet: TradableWallet,
		defaultWallet: TradableWallet,
		isMultichainEnabled?: boolean,
		depositTradable?: PaymentsCryptocoin,
		hasCancelOrder = false,
		isMarginEnabled: boolean = false,
		isMarginBuyEnabled: boolean = false
	): ActionButtonConfig[] {
		const actions = tradable.getTradableActions().map((action) => {
			if (action === ButtonAction.DEPOSIT && isMultichainEnabled && depositTradable) {
				return {
					action: action as ButtonAction,
					isDisabled: !depositTradable.deposit_operational,
					tooltip: this.getTooltipForDeposit(depositTradable.deposit_operational),
					tooltipAmount: this.getAmountForTooltip(action, tradable, fiat)
				};
			}
			return {
				action: action as ButtonAction,
				isDisabled: this.isActionDisabled(
					action,
					tradable,
					walletBalances,
					fiat,
					highestBalanceWallet,
					defaultWallet
				),
				tooltip: this.getTooltipForAction(
					action,
					tradable,
					walletBalances,
					fiat,
					highestBalanceWallet,
					defaultWallet
				),
				tooltipAmount: this.getAmountForTooltip(action, tradable, fiat)
			};
		});

		if (tradable.getCurrencyType() !== INDEX_TYPE) {
			actions.push({
				action: ButtonAction.ADD_ALERT,
				tooltip: "",
				isDisabled: false,
				tooltipAmount: ""
			});
		}

		if (isMarginEnabled && tradable instanceof Cryptocoin && !(tradable instanceof SecurityToken)) {
			const marginTradingAction = {
				action: ButtonAction.MARGIN_TRADE,
				tooltip: "",
				isDisabled: !isMarginBuyEnabled,
				tooltipAmount: ""
			};
			actions.unshift(marginTradingAction);
		}

		//extra optional action to cancel cancel order
		if (hasCancelOrder) {
			actions.push({
				action: ButtonAction.CANCEL_ORDER,
				tooltip: "",
				isDisabled: false,
				tooltipAmount: ""
			});
		}

		return actions;
	}

	private isActionDisabled(
		action: string,
		tradable: Tradable,
		walletBalances: WalletsBalanceStatus,
		fiat: Fiat,
		highestBalanceWallet: TradableWallet,
		defaultWallet: TradableWallet
	): boolean {
		if (!highestBalanceWallet) {
			return true;
		}
		if (action === TooltipTradableAction.SWAP) {
			if (!this.userService.user.isVerified()) {
				return true;
			}

			if (!this.hasEnoughAssetsToSwap(tradable, fiat)) {
				return true;
			}

			if (!defaultWallet) {
				return false;
			}

			return this.tooltipService.isDisabled(action, tradable, fiat, defaultWallet);
		}

		if (action === TooltipTradableAction.SEND) {
			if (!this.userService.user.isVerified() || !tradable.isWithdrawActive()) {
				return true;
			}

			const walletBalance: WalletBalance = walletBalances.balances.find(
				(balance) => balance.walletGuid === highestBalanceWallet.getId()
			);
			return this.sendEntryPoint.getSendStatusForCryptoCoin(
				tradable,
				walletBalance,
				walletBalances.hasUnconfirmedBalance
			).isDisabled;
		}
		return this.tooltipService.isDisabled(action, tradable, fiat, highestBalanceWallet);
	}

	private getTooltipForDeposit(isDepositActive: boolean): string {
		if (this.userService.isVerificationMandatory) {
			return "action_deposit_unverified_tooltip";
		}
		if (!isDepositActive) {
			return "action_deposit_tooltip";
		}
		return "";
	}

	private getTooltipForAction(
		action: string,
		tradable: Tradable,
		walletBalances: WalletsBalanceStatus,
		fiat: Fiat,
		highestBalanceWallet: TradableWallet,
		defaultWallet: TradableWallet
	): string {
		if (!highestBalanceWallet) {
			return "";
		}
		const tooltipOptions: TooltipCoinOptions = {
			action: action as TooltipTradableAction,
			tradable: tradable,
			fiat: fiat,
			checkAllTradableWallets: false,
			tradableWallet: highestBalanceWallet
		};

		if (action === TooltipTradableAction.SEND) {
			const walletBalance: WalletBalance = walletBalances.balances.find(
				(balance: WalletBalance) => balance.walletGuid === highestBalanceWallet.getId()
			);

			return this.sendEntryPoint.getSendStatusForCryptoCoin(
				tradable,
				walletBalance,
				walletBalances.hasUnconfirmedBalance
			).message;
		}

		if (
			!this.isActionDisabled(
				tooltipOptions.action,
				tooltipOptions.tradable || null,
				walletBalances,
				fiat,
				highestBalanceWallet,
				defaultWallet
			)
		) {
			return "";
		}

		return this.tooltipService.getTooltip(tooltipOptions);
	}

	private getAmountForTooltip(action: string, tradable: Tradable, fiat: Fiat): string {
		const tooltipOptions: TooltipCoinOptions = {
			action: action as TooltipTradableAction,
			tradable: tradable,
			fiat: fiat
		};

		if (action === TooltipTradableAction.SEND) {
			return "" + tradable.getAssetSettings().getMinWithdrawal();
		}

		return this.tooltipService.getAmountForTooltip(tooltipOptions);
	}

	/////// PRIVATE FUNCTIONS FOR CALCULATIONS ////////

	private trade(
		action: string,
		tradable: Tradable,
		fiat: Fiat,
		multichainFF: boolean,
		checkoutFF = false,
		newTermsAndConditions = false
	): void {
		if (action === ButtonAction.ADD_ALERT) {
			this.openAddPriceAlert(tradable, fiat);
		} else {
			const wallet = this.tradableWalletListService.getHighestBalanceWallet(tradable.getId());
			this.onActionClick(action, wallet, tradable, fiat, multichainFF, checkoutFF, newTermsAndConditions);
		}
	}

	// BEST 2.0 opt-in terms
	private onActionClick(
		action: string,
		tradableWallet: TradableWallet = null,
		tradable: Tradable,
		fiat: Fiat,
		isSendMultichain: boolean,
		checkoutFF = false,
		newTermsAndConditions = false
	): void {
		if (
			!newTermsAndConditions &&
			(tradable?.getId() === TOKEN_BEST_ID || tradableWallet?.getCurrencyId() === TOKEN_BEST_ID) &&
			this.accountService.showBest2TermsOnBestTrade() &&
			["buy", "deposit"].some((a) => a === action)
		) {
			this.handleBest2Terms().subscribe((res) => {
				if (res.data === "accepted") {
					this.onActionClick(
						action,
						tradableWallet,
						tradable,
						fiat,
						isSendMultichain,
						false,
						newTermsAndConditions
					);
				}
			});
			return;
		}

		let walletList: TradableWallet[] = this.availableWallets(action, tradable, fiat);

		// BUY starts here
		if (checkoutFF && action === TooltipTradableAction.BUY) {
			// New CHECKOUT BUY flow
			this.modalService.pop();
			const assetId = tradable?.getId();
			this.checkoutEntryHelper.onTradableSelect(assetId);
		} else {
			// If we have more than 1 wallet we need to make sure that dummy wallet isn't one of them
			if (walletList.length > 1) {
				walletList = walletList.filter((w) => !w.isNew());
			}

			// If we still have more than 1 wallet, then we need to show the multiple wallets dialog
			if (walletList.length > 1) {
				const selectionModal: Modal<WalletSelectionModalComponent> = this.modalService.create(
					WalletSelectionModalComponent,
					{
						tradableWallets: walletList,
						tradableId: tradable.getId()
					}
				);
				selectionModal.present();
				selectionModal.onDismiss.subscribe((res) => {
					const selectedWallet: TradableWallet = res["data"];
					if (!selectedWallet) {
						return;
					}
					this.superOnActionClick(
						action,
						selectedWallet,
						tradable,
						fiat,
						isSendMultichain,
						newTermsAndConditions
					);
				});
			} else {
				this.superOnActionClick(action, walletList[0], tradable, fiat, isSendMultichain, newTermsAndConditions);
			}
		}
	}

	// BEST 2.0 opt-in terms
	private superOnActionClick(
		action: string,
		wallet: TradableWallet = null,
		tradable: Tradable,
		fiat: Fiat,
		isSendMultichain: boolean,
		newTermsAndConditions = false
	): void {
		if (
			(wallet?.getCurrencyId() === TOKEN_BEST_ID || tradable?.getId() === TOKEN_BEST_ID) &&
			["buy", "savings", "deposit"].some((a) => a === action) &&
			this.accountService.showBest2TermsOnBestTrade()
		) {
			if (newTermsAndConditions) {
				this.onActionClick(action, wallet, tradable, fiat, isSendMultichain, false, newTermsAndConditions);
			} else {
				this.handleBest2Terms().subscribe((res) => {
					if (res.data === "accepted") {
						this.onActionClick(
							action,
							wallet,
							tradable,
							fiat,
							isSendMultichain,
							false,
							newTermsAndConditions
						);
					}
				});
			}
			return;
		}

		const offer = new Offer();
		offer.setDirectTradableWalletOffer(true);
		offer.setIndexTrade(wallet instanceof IndexWallet);
		offer.setWalletId(wallet.getId());
		offer.setCurrencyId(wallet.getCurrencyId());

		if (action === TooltipTradableAction.BUY && !this.userFiatFundsService.hasFiatWalletsWithFunds()) {
			this.fiatDepositEntryService.openDepositNow();
		}

		if (
			(action === TooltipTradableAction.BUY && this.userFiatFundsService.hasFiatWalletsWithFunds()) ||
			action === TooltipTradableAction.SELL
		) {
			this.tradeAction(action, offer);
		} else {
			if (action === TooltipTradableAction.SWAP) {
				if (this.tooltipService.isUnderMinSell(tradable, wallet, fiat) || !tradable.isSellActive()) {
					this.modalService
						.create(TradeSwapComponent, {
							externalLaunchOptions: {
								wallet,
								usedFor: "right"
							}
						})
						.present();
				} else {
					this.modalService
						.create(TradeSwapComponent, {
							externalLaunchOptions: { wallet }
						})
						.present();
				}
			}

			if (action === TooltipTradableAction.SEND && (!tradable || tradable.isWithdrawActive())) {
				const sendOffer = new SendTransaction();
				sendOffer.setWallet(wallet);
				sendOffer.setDirectTradableWalletOffer(true);
				if (isSendMultichain) {
					this.sendEntryPoint.openMultichainSendFlow(wallet, tradable);
				} else {
					this.sendEntryPoint.openCryptoSendModal({ transaction: sendOffer });
				}
			} else if (
				action === TooltipTradableAction.TRANSFER &&
				(!tradable || this.getAssetWalletsForTransfer(wallet, tradable))
			) {
				const t = new InternalTransaction();
				t.setFrom(wallet.getId());
				t.setSendFlowAccess(false);
				this.modalService.create(TransferModalComponent, { data: t }).present();
			}

			if (action === TooltipTradableAction.DEPOSIT) {
				this.modalService
					.create(CryptowalletDetailsComponent, { data: { wallet, cryptocoinIcon: tradable.getLogo() } })
					.present();
			}

			if (action === TooltipTradableAction.SAVINGS) {
				offer.setType(TRADE_TYPE.RECURRING_BUY);

				const callback: Function = () =>
					this.modalService.create(TradeSavingsComponent, { data: offer }).present();
				if (this.stockTestService.needsTest(offer.getCurrencyId())) {
					this.stockTestHelperService.startTest(callback);
				} else {
					callback();
				}
			}
		}
	}

	private getAssetWalletsForTransfer(wallet: TradableWallet, tradable: Tradable): boolean {
		const wallets: TradableWallet[] = this.tradableWalletListService.getWalletsByTradableId(wallet.getCurrencyId());
		if (wallets) {
			return wallets.length > 1 && tradable.isTransferActive();
		}
	}

	private tradeAction(action: string, offer: Offer): void {
		const tradeAction = TRADE_ACTION[action];

		offer.setType(tradeAction.type);

		const showModal: () => void = () => this.modalService.create(tradeAction.component, { data: offer }).present();

		this.stockTestService.needsTest(offer.getCurrencyId())
			? this.stockTestHelperService.startTest(showModal)
			: showModal();
	}

	private availableWallets(action: string, tradable: Tradable, fiat: Fiat): TradableWallet[] {
		return this.tradableWalletListService
			.getWalletsByTradableId(tradable.getId())
			.filter((wallet) => !this.tooltipService.isDisabled(action, tradable, fiat, wallet));
	}

	// BEST 2.0 opt-in terms
	private handleBest2Terms(): EventEmitter<{ data: string; role: string }> {
		const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, { showBackdrop: false });
		best2TermsModal.present();
		return best2TermsModal.onDismiss;
	}

	public openAddPriceAlert(tradable: Tradable, fiat: Fiat): void {
		const priceAlert = new PriceAlert();
		priceAlert.setCurrencyId(tradable.getId());
		priceAlert.setFiatId(fiat.getId());
		this.modalService
			.create(PricewatchAddModalComponent, { data: priceAlert }, { showCloseButton: true })
			.present();
	}

	private hasEnoughAssetsToSwap(tradable: Tradable, fiat: Fiat): boolean {
		return this.tooltipService.hasOtherAssetsToSwap(tradable, fiat);
	}
}
